import GreatDealTag from '@carvana/ds-icons/TagFilledCvna';
import LinkButton from '@carvana/ds-link-button';
import { icon } from '@carvana/ds-styles';
import Switch from '@carvana/ds-switch';
import Clickable from '@carvana/merch-components/Clickable';
import cx from 'classnames';
import { useContext, useEffect } from 'react';
import { appAssetsCdnUrl } from '~/constants/environment';
import { useIsMobile } from '~/context/common/DeviceContext';
import { useConfiguration } from '~/context/common/StaticContext';
import TradeContext, { useSelectedTrade } from '~/context/common/TradeContext';
import ModalContext from '~/context/srp/ModalContext';
import useTradeVehicleImage from '~/hooks/common/useTradeVehicleImage';
import CoinIcon from '~/icons/CoinIcon';
import useTradeIncentiveTitleCopy from './useTradeIncentiveTitleCopy';
import { PromoBanners } from '../constants';
import { useSendViewedBannerEvent } from '../hooks';
import {
  TradeIncentiveCarImage,
  TradeIncentiveLeftContent,
  TradeIncentiveRightContent,
  TradeIncentiveSwooshImage,
  TradeIncentiveTitleWrapper,
  wrapperStyles
} from '../PromoBanner.styles';
import { getBannerName } from '../utils';

export default function TradeIncentiveBanner() {
  const { shouldUseTitleCasing } = useConfiguration();
  const bannerName = getBannerName(PromoBanners.WSYS);
  const { setIsTradeIncentiveModalOpen } = useContext(ModalContext);
  const { isTradeInApplied, toggleAppliedTrade } = useContext(TradeContext);
  const isMobile = useIsMobile();
  const selectedTrade = useSelectedTrade();
  const sendViewedBannerEvent = useSendViewedBannerEvent();
  const titleCopy = useTradeIncentiveTitleCopy();
  const { imageUrl, showImage } = useTradeVehicleImage('TradeIncentiveBanner');

  const { id, tradeIncentive } = selectedTrade ?? {};
  const hasTradeIncentive = Boolean(tradeIncentive);

  const swooshAsset = `${appAssetsCdnUrl}/images/wsys/${
    hasTradeIncentive ? 'ellipse-banner' : 'ellipse'
  }-${isMobile ? 'mobile' : 'desktop'}.png`;

  const carAsset = `${appAssetsCdnUrl}/images/wsys/vehicle-${
    isMobile ? 'mobile' : 'desktop'
  }.png`;

  useEffect(() => {
    sendViewedBannerEvent(bannerName);
  }, [bannerName, sendViewedBannerEvent]);

  if (!titleCopy) return null;

  if (hasTradeIncentive) {
    return (
      <div
        className={cx(
          wrapperStyles,
          'bg-background-default border border-border-subtle sm:h-[176px] justify-between gap-md'
        )}
      >
        <TradeIncentiveLeftContent>
          <div className="inline-flex items-center gap-sm rounded-full bg-background-success px-md py-[3px] text-text-inverse t-label-xs">
            <CoinIcon />
            Trade Boost
          </div>
          <TradeIncentiveTitleWrapper>
            {titleCopy}{' '}
            <Clickable
              element="span"
              className="underline t-body-sm whitespace-nowrap"
              data-adobe-id="learn-more-about-trade-incentive"
              data-analytics-id="Banner"
              data-analytics-attributes={JSON.stringify({
                category: 'WSYS',
                label: 'open modal',
                text: isMobile ? 'Details' : 'Learn More'
              })}
              data-testid="trade-incentive-learn-more"
              onClick={() => setIsTradeIncentiveModalOpen(true)}
            >
              {isMobile ? 'Details' : 'Learn More'}
            </Clickable>
          </TradeIncentiveTitleWrapper>
          <div className="flex items-center gap-md">
            <div className="t-body-sm whitespace-nowrap text-text-strong">
              Show pricing with trade-in
            </div>
            <Switch
              aria-label={
                isTradeInApplied
                  ? 'Include trade-in pricing'
                  : 'Exclude trade-in pricing'
              }
              data-analytics-id="Banner"
              data-analytics-attributes={JSON.stringify({
                category: 'WSYS',
                label: 'toggle trade'
              })}
              data-testid="wsys-trade-in-toggle"
              checked={isTradeInApplied}
              onChange={toggleAppliedTrade}
            />
          </div>
        </TradeIncentiveLeftContent>
        {isMobile ? (
          <>
            <img
              className="absolute z-[2] h-full top-0 right-0"
              src={swooshAsset}
              alt="wsys swoosh asset"
            />
            {showImage && (
              <img
                className="absolute -translate-y-[35px] z-[3] w-[210px] right-0 translate-x-[60px]"
                src={imageUrl}
                alt="wsys car asset"
              />
            )}
          </>
        ) : (
          <TradeIncentiveRightContent>
            <TradeIncentiveSwooshImage
              src={swooshAsset}
              alt="wsys swoosh asset"
            />
            {showImage && (
              <TradeIncentiveCarImage src={imageUrl} alt="wsys car asset" />
            )}
          </TradeIncentiveRightContent>
        )}
      </div>
    );
  }

  return (
    <div className={cx(wrapperStyles, 'bg-background-strong-active')}>
      <div className="relative z-[5] flex w-full min-w-[180px] flex-col items-start justify-center py-2xl pl-2xl  sm:pl-4xl lg:py-4xl ">
        <div className="flex items-center gap-sm">
          <GreatDealTag size={16} color={icon.inverse} />
          <div className="text-text-inverse t-label-sm">Bundle & Save</div>
        </div>
        <div className="mb-lg mt-md w-full max-w-[calc(100%-108px)] text-text-inverse t-heading-sm sm:max-w-[calc(100%-240px)] sm:t-heading-xl lg:max-w-[calc(100%-400px)] lg:t-heading-2xl">
          {titleCopy}
        </div>
        <div className="flex items-center gap-lg">
          <div className="flex items-center gap-md">
            <div className="flex items-center gap-sm text-text-inverse t-body-sm">
              Include{' '}
              <span className="hidden flex-nowrap sm:flex">trade-in</span>
            </div>
            <Switch
              aria-label={
                isTradeInApplied
                  ? 'Include trade-in pricing'
                  : 'Exclude trade-in pricing'
              }
              data-analytics-id="Banner"
              data-analytics-attributes={JSON.stringify({
                category: 'WSYS',
                label: 'toggle trade'
              })}
              data-testid="wsys-trade-in-toggle"
              checked={isTradeInApplied}
              onChange={toggleAppliedTrade}
            />
          </div>
          <div className=" h-16 border-r border-border-inverse-transparent" />
          <LinkButton
            data-adobe-id="learn-more-about-trade-incentive"
            data-analytics-id="Banner"
            data-analytics-attributes={JSON.stringify({
              category: 'WSYS',
              label: 'open modal',
              text: 'Learn more'
            })}
            data-testid="trade-incentive-learn-more"
            onClick={() => setIsTradeIncentiveModalOpen(true)}
            size="sm"
            variant="inverse"
          >
            Learn More
          </LinkButton>
        </div>
      </div>
      <img
        className="absolute right-0 z-[2] h-[98%] w-[120px] sm:h-[80%] sm:w-[490px] lg:h-[98%]"
        src={swooshAsset}
        alt="wsys swoosh asset"
      />
      <img
        className="absolute right-0 z-[3] w-[108px] sm:top-1/2 sm:w-[240px] sm:-translate-y-1/2 lg:w-[400px]"
        src={carAsset}
        alt="wsys car asset"
      />
    </div>
  );
}
